import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Download, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import { KvRow, Panel, StatusBadge, btnPrimary, btnSecondary, inputCls, money, shortDate } from "@/components/admin/ui";

export function AdminBusinessList() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState(null);

  useEffect(() => {
    adminApi.get("/business-accounts").then((r) => setAccounts(r.data.accounts)).catch(() => {});
  }, []);

  return (
    <div className="space-y-4" data-testid="admin-business">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Business</h1>
        <p className="mt-0.5 text-sm text-slate">Entreprises, hôtels, cliniques, conciergeries — mêmes données que l’espace MILES Business.</p>
      </div>
      <Panel testId="business-list">
        {!accounts && <div className="h-40 animate-pulse rounded bg-cloud" />}
        {accounts?.length === 0 && <p className="py-8 text-center text-sm text-slate">Aucun compte Business.</p>}
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {accounts?.map((a) => (
            <button key={a.id} type="button" onClick={() => navigate(`/admin/app/business/${a.id}`)} data-testid={`org-${a.id}`}
              className="rounded border border-slate/15 bg-white p-4 text-left transition-colors hover:border-miles/40">
              <p className="font-semibold text-ink">{a.name}</p>
              <p className="mt-1 text-sm text-slate">{a.billing_email}</p>
              <div className="mt-3 flex justify-between font-mono text-xs text-slate">
                <span>{a.rides_count} courses</span>
                <span>{a.users_count} utilisateur(s)</span>
                <span className="font-semibold text-ink">{money(a.revenue_total)}</span>
              </div>
            </button>
          ))}
        </div>
      </Panel>
    </div>
  );
}

export function AdminBusinessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [commercial, setCommercial] = useState(null);
  const [commForm, setCommForm] = useState({ commission_enabled: false, rate: "", payout_frequency: "monthly", min_payout: "" });
  const [payouts, setPayouts] = useState([]);
  const [payForm, setPayForm] = useState({});

  const load = useCallback(() => {
    adminApi.get(`/business-accounts/${id}`).then((r) => setData(r.data)).catch(() => navigate("/admin/app/business"));
    adminApi.get(`/partners/${id}/commercial`).then((r) => {
      setCommercial(r.data);
      const c = r.data.commercial || {};
      setCommForm({
        commission_enabled: Boolean(c.commission_enabled),
        rate: c.rate ?? "",
        payout_frequency: c.payout_frequency || "monthly",
        min_payout: c.min_payout ?? "",
      });
    }).catch(() => {});
    adminApi.get("/partners/payouts").then((r) => setPayouts(r.data.filter((p) => p.org_id === id))).catch(() => {});
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const saveCommercial = async () => {
    try {
      await adminApi.patch(`/partners/${id}/commercial`, {
        commission_enabled: commForm.commission_enabled,
        rate: commForm.rate === "" ? null : Number(commForm.rate),
        payout_frequency: commForm.payout_frequency,
        min_payout: commForm.min_payout === "" ? null : Number(commForm.min_payout),
      });
      toast.success("Conditions commerciales enregistrées");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const createPayout = async () => {
    try {
      await adminApi.post("/partners/payouts", { org_id: id });
      toast.success("Payout créé");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const markPaid = async (payoutId) => {
    const f = payForm[payoutId] || {};
    if (!f.payment_date) { toast.error("Date de paiement requise"); return; }
    try {
      await adminApi.post(`/partners/payouts/${payoutId}/paid`, {
        payment_date: f.payment_date, bank_reference: f.bank_reference || null, internal_note: f.internal_note || null,
      });
      toast.success("Payout marqué payé");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const clearHold = async () => {
    await adminApi.post(`/partners/${id}/payout-hold/clear`).catch(() => {});
    toast.success("Hold levé — coordonnées vérifiées");
    load();
  };

  if (!data) return <div className="h-96 animate-pulse rounded-md border border-slate/15 bg-white" />;
  const org = data.organization;

  return (
    <div className="space-y-4" data-testid="admin-business-detail">
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => navigate(-1)} className={btnSecondary}><ArrowLeft className="h-4 w-4" strokeWidth={1.5} /></button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink" data-testid="org-name">{org.name}</h1>
          <p className="mt-0.5 text-sm text-slate">Facturation : {org.billing_email} · depuis le {shortDate(org.created_at)}</p>
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-4">
          <Panel title={`Utilisateurs (${data.users.length})`} testId="org-users">
            {data.users.map((u) => (
              <div key={u.id} className="flex items-center justify-between border-b border-slate/10 py-2 text-sm last:border-0">
                <span className="text-ink">{u.name}</span>
                <span className="rounded bg-miles-ice px-2 py-0.5 text-xs font-medium text-miles">{u.role}</span>
              </div>
            ))}
          </Panel>
          <Panel title={`Passagers récurrents (${data.passengers.length})`} testId="org-passengers">
            {data.passengers.map((p) => (
              <div key={p.id} className="border-b border-slate/10 py-2 text-sm last:border-0">
                <p className="text-ink">{p.first_name} {p.last_name}</p>
                <p className="font-mono text-xs text-slate">{p.phone}</p>
              </div>
            ))}
          </Panel>
          <Panel title="Configuration" testId="org-config">
            <KvRow label="Centres de coûts">{(org.cost_centers || []).join(", ") || "—"}</KvRow>
            <KvRow label="Facturation">Mensuelle</KvRow>
          </Panel>

          {/* ---------- Commercial : commissions ---------- */}
          <Panel title="Commercial — Commissions" testId="org-commercial">
            {!commercial ? <div className="h-32 animate-pulse rounded bg-cloud" /> : (
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm text-ink" data-testid="comm-enabled-row">
                  <input type="checkbox" className="h-4 w-4 accent-miles" checked={commForm.commission_enabled}
                    onChange={(e) => setCommForm({ ...commForm, commission_enabled: e.target.checked })} data-testid="comm-enabled" />
                  Commissions activées
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-xs text-slate">Taux partenaire (plafond {commercial.max_rate} %)</p>
                    <input type="number" min="0" max={commercial.max_rate} step="0.5" className={`${inputCls} mt-1`}
                      value={commForm.rate} onChange={(e) => setCommForm({ ...commForm, rate: e.target.value })}
                      data-testid="comm-rate" />
                  </div>
                  <div>
                    <p className="text-xs text-slate">Seuil de versement (CHF)</p>
                    <input type="number" min="0" className={`${inputCls} mt-1`}
                      value={commForm.min_payout} onChange={(e) => setCommForm({ ...commForm, min_payout: e.target.value })}
                      data-testid="comm-min-payout" />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate">Fréquence de versement</p>
                  <select className={`${inputCls} mt-1`} value={commForm.payout_frequency}
                    onChange={(e) => setCommForm({ ...commForm, payout_frequency: e.target.value })} data-testid="comm-frequency">
                    <option value="weekly">Hebdomadaire</option>
                    <option value="biweekly">Bimensuelle</option>
                    <option value="monthly">Mensuelle</option>
                    <option value="manual">Manuelle</option>
                  </select>
                </div>
                <button type="button" onClick={saveCommercial} className={btnPrimary} data-testid="comm-save">
                  Enregistrer
                </button>
                <div className="border-t border-slate/10 pt-3">
                  <KvRow label="Disponible"><span className="font-mono font-semibold">{money(commercial.wallet?.available)}</span></KvRow>
                  <KvRow label="En attente">{money(commercial.wallet?.pending)}</KvRow>
                  <KvRow label="Versé cette année">{money(commercial.wallet?.paid_this_year)}</KvRow>
                  <KvRow label="IBAN partenaire">{commercial.payout_details_masked || "—"}</KvRow>
                </div>
                {commercial.payout_hold && (
                  <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-700" data-testid="payout-hold-box">
                    <p className="flex items-center gap-1.5 font-semibold"><ShieldAlert className="h-3.5 w-3.5" /> Hold actif — coordonnées bancaires récemment modifiées</p>
                    <button type="button" onClick={clearHold} className={`${btnSecondary} mt-2 h-8 px-3 text-xs`} data-testid="payout-hold-clear">
                      <BadgeCheck className="h-3.5 w-3.5" /> Vérifié — lever le hold
                    </button>
                  </div>
                )}
                {(commercial.commercial?.rate_history || []).length > 0 && (
                  <div className="border-t border-slate/10 pt-3" data-testid="rate-history">
                    <p className="text-xs font-semibold text-slate">Historique des taux</p>
                    {commercial.commercial.rate_history.map((h, i) => (
                      <p key={i} className="mt-1 font-mono text-xs text-slate">
                        {h.rate} % · {shortDate(h.from)} → {shortDate(h.to)} · {h.by}
                      </p>
                    ))}
                    <p className="mt-1 font-mono text-xs text-ink">
                      {commercial.commercial.rate} % · depuis {shortDate(commercial.commercial.rate_since)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </Panel>

          {/* ---------- Payouts ---------- */}
          <Panel title="Payouts" testId="org-payouts"
            action={<button type="button" onClick={createPayout} className={`${btnPrimary} h-8 px-3 text-xs`} data-testid="payout-create">Créer un payout</button>}>
            {payouts.length === 0 && <p className="text-xs text-slate">Aucun versement.</p>}
            {payouts.map((p) => (
              <div key={p.id} className="border-b border-slate/10 py-2.5 text-sm last:border-0" data-testid={`payout-${p.number}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs font-bold text-ink">{p.number}</span>
                  <span className="font-mono font-semibold">{money(p.amount)}</span>
                  <StatusBadge status={p.status === "paid" ? "completed" : "booked"} />
                </div>
                <p className="mt-0.5 text-xs text-slate">{p.period} · {p.count} commissions</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/partners/payouts/${p.id}/statement`}
                    target="_blank" rel="noreferrer" className="text-xs font-semibold text-miles hover:underline"
                    data-testid={`payout-statement-${p.number}`}>
                    <Download className="mr-1 inline h-3 w-3" /> Relevé PDF
                  </a>
                  {p.status !== "paid" && (
                    <>
                      <input type="date" className="h-8 rounded-lg border border-slate/20 px-2 text-xs" data-testid={`payout-date-${p.number}`}
                        onChange={(e) => setPayForm({ ...payForm, [p.id]: { ...(payForm[p.id] || {}), payment_date: e.target.value } })} />
                      <input placeholder="Réf. bancaire" className="h-8 w-28 rounded-lg border border-slate/20 px-2 text-xs" data-testid={`payout-ref-${p.number}`}
                        onChange={(e) => setPayForm({ ...payForm, [p.id]: { ...(payForm[p.id] || {}), bank_reference: e.target.value } })} />
                      <button type="button" onClick={() => markPaid(p.id)} className={`${btnPrimary} h-8 px-3 text-xs`} data-testid={`payout-paid-${p.number}`}>
                        Marquer payé
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </Panel>
        </div>
        <div className="lg:col-span-8">
          <Panel title={`Réservations (${data.bookings.length})`} testId="org-bookings">
            <div className="divide-y divide-slate/10">
              {data.bookings.map((b) => (
                <button key={b.id} type="button" onClick={() => navigate(`/admin/app/bookings/${b.id}`)} data-testid={`org-ride-${b.tracking_code}`}
                  className="flex w-full items-center justify-between gap-3 py-2.5 text-left text-sm hover:bg-miles-ice/30">
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="font-mono font-semibold text-miles">{b.tracking_code}</span>
                    <span className="hidden font-mono text-xs text-slate sm:inline">{b.date}</span>
                    <span className="truncate text-ink">{b.pickup} → {b.dropoff || "à l’heure"}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <StatusBadge status={b.display_status} />
                    <span className="font-mono text-ink">{money(b.price_chf)}</span>
                  </span>
                </button>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
